import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/scripts/AuthContext";
import { useOrgFilter } from "@/scripts/OrgFilterContext";
import { API_BASE_URL } from "@/lib/apiBase";
import {
  ORG_SEARCH_GROUP_LABELS,
  ORG_SEARCH_GROUP_ORDER,
  flattenOrgSearchHits,
} from "@/lib/orgSearchQuery";

/**
 * Full org search results: /o/:orgId/search?q=
 */
const OrgSearchPage = () => {
  const { orgId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { setSelectedOrgId } = useOrgFilter();
  const q = (searchParams.get("q") || "").trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Route → workspace only (omit selectedOrgId so switcher isn't fought mid-navigation).
  useEffect(() => {
    if (orgId) {
      setSelectedOrgId(orgId);
    }
  }, [orgId, setSelectedOrgId]);

  useEffect(() => {
    if (!accessToken || !orgId || !q) {
      setLoading(false);
      setResult(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(
          `${API_BASE_URL}/org/${encodeURIComponent(orgId)}/search`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
            params: { q, limit_per_type: 25 },
          }
        );
        if (!cancelled) setResult(res.data || null);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message || "Search failed. Try again."
          );
          setResult(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, orgId, q]);

  const totalHits = useMemo(
    () => flattenOrgSearchHits(result?.groups).length,
    [result]
  );

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6">
      <div className="mb-6 flex items-start gap-3">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Back"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Search className="h-5 w-5 text-muted-foreground shrink-0" />
            <span className="truncate">
              {q ? `Results for “${q}”` : "Search"}
            </span>
          </h1>
        </div>
      </div>

      {!q ? (
        <p className="text-sm text-muted-foreground">
          Type a query in the header search bar, or add{" "}
          <code className="text-xs">?q=</code> to this URL.
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground py-8">Searching…</p>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive py-4">{error}</p>
      ) : null}

      {!loading && !error && q && totalHits === 0 ? (
        <p className="text-sm text-muted-foreground py-8">
          No matches in this organization.
        </p>
      ) : null}

      {!loading &&
        result &&
        ORG_SEARCH_GROUP_ORDER.map((key) => {
          const hits = result.groups?.[key];
          if (!Array.isArray(hits) || hits.length === 0) return null;
          return (
            <section key={key} className="mb-8">
              <h2 className="mb-3 text-sm font-medium uppercase tracking-wide text-muted-foreground">
                {ORG_SEARCH_GROUP_LABELS[key] || key}
              </h2>
              <ul className="divide-y divide-border border-y border-border">
                {hits.map((hit) => (
                  <li key={`${hit.type}-${hit.id}`}>
                    <Link
                      to={hit.href}
                      className="block py-3 hover:bg-muted/40 -mx-2 px-2 rounded-sm"
                    >
                      <div className="font-medium text-sm">{hit.title}</div>
                      {hit.subtitle ? (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {hit.subtitle}
                        </div>
                      ) : null}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
    </div>
  );
};

export default OrgSearchPage;
